import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Section = ({ title, children }) => (
  <div className="mb-8">
    {title && <h2 className="text-2xl font-black text-white mb-3">{title}</h2>}
    <div className="text-gray-300 leading-relaxed space-y-3">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1 text-gray-400">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white py-16 px-4" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/">
          <Button
            variant="ghost"
            className="text-gray-400 hover:text-white mb-8"
            data-testid="privacy-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 mb-2">
            Privacy Policy
          </h1>
          <p className="text-gray-500 mb-10">Last Updated: July 23, 2026</p>

          <Section>
            <p>
              Welcome to Quantum Shop ("we," "us," or "our"). This Privacy Policy explains how we
              collect, use, store, and protect information when you visit or use our website and
              purchase digital game items and related products through our online store (the
              "Service").
            </p>
            <p>By using our Service, you agree to the practices described in this Privacy Policy.</p>
          </Section>

          <Section title="1. Information We Collect">
            <p>We may collect the following types of information:</p>
            <h3 className="text-lg font-bold text-purple-400 mt-4">Information You Provide</h3>
            <p>When you create an account, place an order, or contact us, we may collect:</p>
            <List
              items={[
                'Your name or username',
                'Email address',
                'Account information',
                'Billing and transaction information',
                'Game usernames, player IDs, or other information needed to deliver your purchase',
                'Information you provide when contacting customer support',
              ]}
            />
            <h3 className="text-lg font-bold text-purple-400 mt-4">Payment Information</h3>
            <p>
              Payments may be processed by third-party payment providers. We generally do not store
              complete credit card or debit card numbers on our own systems. Payment providers may
              collect and process payment information according to their own privacy policies.
            </p>
            <h3 className="text-lg font-bold text-purple-400 mt-4">Automatically Collected Information</h3>
            <p>When you access our website, we may automatically collect certain information, such as:</p>
            <List
              items={[
                'IP address',
                'Browser type and device information',
                'Operating system',
                'Pages you visit',
                'Date and time of visits',
                'General website usage information',
                'Cookies and similar technologies',
              ]}
            />
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We may use collected information to:</p>
            <List
              items={[
                'Process and deliver orders',
                'Provide customer support',
                'Manage user accounts',
                'Communicate with you about your orders',
                'Improve and maintain our website',
                'Prevent fraud, abuse, and unauthorized activity',
                'Protect the security of our Service',
                'Comply with legal obligations',
                'Send promotional communications when permitted by law and, where required, with your consent',
              ]}
            />
          </Section>

          <Section title="3. How We Share Your Information">
            <p>We do not sell your personal information for money.</p>
            <p>
              We may share information with trusted third parties when necessary to operate our
              business, including:
            </p>
            <List
              items={[
                'Payment processors',
                'Website hosting and infrastructure providers',
                'Order fulfillment or delivery services',
                'Customer support providers',
                'Security and fraud prevention services',
                'Analytics providers',
              ]}
            />
            <p>
              We may also disclose information when required by law, legal process, or government
              request, or when necessary to protect our rights, users, or the security of our
              Service.
            </p>
          </Section>

          <Section title="4. Cookies and Tracking Technologies">
            <p>We may use cookies and similar technologies to:</p>
            <List
              items={[
                'Keep you signed in',
                'Remember preferences',
                'Understand how visitors use our website',
                'Improve website performance',
                'Help prevent fraud and abuse',
              ]}
            />
            <p>
              You may be able to control cookies through your browser settings. Disabling certain
              cookies may affect the functionality of the Service.
            </p>
          </Section>

          <Section title="5. Data Retention">
            <p>
              We retain personal information only for as long as reasonably necessary to provide our
              services, maintain business and transaction records, resolve disputes, prevent fraud,
              and comply with legal obligations.
            </p>
            <p>
              When information is no longer needed, we may delete or securely dispose of it, subject
              to applicable laws.
            </p>
          </Section>

          <Section title="6. Data Security">
            <p>
              We take reasonable administrative, technical, and organizational measures to help
              protect personal information from unauthorized access, loss, misuse, or disclosure.
            </p>
            <p>
              However, no online service can guarantee complete security. You should use caution
              when sharing personal information online and keep your account credentials
              confidential.
            </p>
          </Section>

          <Section title="7. Third-Party Services and Links">
            <p>
              Our website may contain links to third-party websites or services. We are not
              responsible for the privacy practices or content of third-party services.
            </p>
            <p>
              We encourage you to review the privacy policies of any third-party websites or
              services you use.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              Our Service is not intended for children under the minimum age required to use online
              services in their jurisdiction. We do not knowingly collect personal information from
              children in violation of applicable law.
            </p>
            <p>
              If you believe a child has provided us with personal information, please contact us so
              we can take appropriate action.
            </p>
          </Section>

          <Section title="9. Your Privacy Rights">
            <p>
              Depending on where you live, you may have rights regarding your personal information,
              which may include the right to:
            </p>
            <List
              items={[
                'Request access to personal information we hold about you',
                'Request correction of inaccurate information',
                'Request deletion of certain personal information',
                'Request a copy of your information',
                'Object to or restrict certain processing',
                'Withdraw consent where processing is based on consent',
              ]}
            />
            <p>
              To exercise applicable privacy rights, contact us using the information provided in the
              "Contact Us" section below. We may need to verify your identity before processing
              certain requests.
            </p>
          </Section>

          <Section title="10. California Privacy Rights">
            <p>
              If you are a California resident, you may have additional rights under applicable
              California privacy laws. These rights may include the ability to request information
              about the categories of personal information we collect, the purposes for which we use
              it, and certain details about how information is shared.
            </p>
            <p>
              We do not sell personal information for monetary consideration. If applicable law
              defines certain data sharing as "selling" or "sharing," you may have additional rights
              regarding those activities.
            </p>
          </Section>

          <Section title="11. Changes to This Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make changes, we will
              update the "Last Updated" date at the top of this policy.
            </p>
            <p>
              We encourage you to review this Privacy Policy periodically to stay informed about how
              we handle personal information.
            </p>
          </Section>

          <Section title="12. Contact Us">
            <p>
              If you have questions about this Privacy Policy or want to exercise applicable privacy
              rights, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-zinc-900 rounded-xl border border-purple-900/30">
              <p className="font-bold text-white">Quantum Shop</p>
              <p>Email: Chingstocks1@gmail.com</p>
              <p>Website: Quantumshop.org</p>
            </div>
            <p className="mt-4">
              By using our Service after changes to this Privacy Policy become effective, you
              acknowledge the updated policy.
            </p>
          </Section>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
